// shell-sdk/src/runtime.js

const runtime = {
    contextsByScope: new Map(),
    capabilitiesByScope: new Map(),
    latestContext: null,
    latestRoute: null,
    latestParams: {},
    config: {},
    traceId: null,
};

export function safeClone(value, fallback = {}) {
    if (value == null) return fallback;

    try {
        return JSON.parse(JSON.stringify(value));
    } catch {
        return fallback;
    }
}

export function normalizeScope(scope = {}) {
    return {
        mfe: scope.mfe,
        version: scope.version,
        island: scope.island || undefined,
        instanceId: scope.instanceId || undefined,
    };
}

export function scopeKey(scope = {}) {
    const normalized = normalizeScope(scope);
    return [
        normalized.mfe || "",
        normalized.version || "",
        normalized.island || "",
        normalized.instanceId || "",
    ].join("::");
}

export function scopeMatches(candidate = {}, target = {}) {
    if (!candidate || !target) return false;
    if (target.mfe && candidate.mfe !== target.mfe) return false;
    if (target.version && candidate.version !== target.version) return false;
    if (target.island && candidate.island !== target.island) return false;
    if (target.instanceId && candidate.instanceId !== target.instanceId) return false;
    return !!target.mfe;
}

function scopeFromContext(context = {}) {
    return normalizeScope(context.to || context.target || context);
}

export function setContext(context) {
    const cloned = safeClone(context, null);
    if (!cloned) return null;

    const scope = scopeFromContext(cloned);
    const key = scopeKey(scope);

    runtime.latestContext = cloned;
    runtime.contextsByScope.set(key, cloned);
    runtime.capabilitiesByScope.set(key, safeClone(cloned.capabilities || {}, {}));
    runtime.traceId = cloned.traceId || runtime.traceId || null;

    if (cloned.route) {
        runtime.latestRoute = safeClone(cloned.route, null);
        runtime.latestParams = safeClone(cloned.route.params || cloned.params || {}, {});
    }

    return cloned;
}

export function setCapabilities(scope, capabilities) {
    const normalized = normalizeScope(scope);
    const key = scopeKey(normalized);
    runtime.capabilitiesByScope.set(key, safeClone(capabilities || {}, {}));

    const context = runtime.contextsByScope.get(key);
    if (context) {
        context.capabilities = safeClone(capabilities || {}, {});
    }
}

export function setRoute(route, params = {}) {
    runtime.latestRoute = route || null;
    runtime.latestParams = safeClone(params || {}, {});
}

export function setConfig(config) {
    runtime.config = safeClone(config || {}, {});
}

export function getContext(scope) {
    if (!scope) return runtime.latestContext;
    return runtime.contextsByScope.get(scopeKey(scope)) || null;
}

export function getCapabilities(scope) {
    if (!scope) return {};
    return runtime.capabilitiesByScope.get(scopeKey(scope)) || {};
}

export function hasCapability(scope, capability) {
    return !!getCapabilities(scope)?.[capability];
}

export function getRoute(scope) {
    return getContext(scope)?.route || runtime.latestRoute;
}

export function getParams(scope) {
    return getRoute(scope)?.params || runtime.latestParams || {};
}

export function getConfig() {
    return runtime.config || {};
}

export function getTraceId(scope) {
    return getContext(scope)?.traceId || runtime.traceId || null;
}
